import React, { useState, useEffect, useLayoutEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../api/client";
import logo from "../assets/images/polyglow-logo.svg";

const API_URL = `/api/auth/admin-panel/users/`;

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
    is_staff: false,
  });

  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(API_URL);
      if (res.status === 401 || res.status === 403) {
        setError("Accès refusé. Vous devez être administrateur.");
        setUsers([]);
      } else if (res.ok) {
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
        setUsers(list);
        setError("");
      } else {
        setError("Erreur chargement données.");
        setUsers([]);
      }
    } catch (err) {
      console.error("fetchUsers error:", err);
      setError("Erreur connexion serveur.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useLayoutEffect(() => {
    document.documentElement.style.overflow = "auto";
    document.body.style.overflow = "auto";
    return () => {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    };
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setMsg(""); setError("");
    try {
      const res = await api.post(API_URL, form);
      if (res.ok) {
        setMsg("Utilisateur créé !");
        setForm({ email: "", username: "", password: "", is_staff: false });
        await fetchUsers();
      } else {
        setError("Erreur: " + JSON.stringify(res.data));
      }
    } catch (err) {
      setError("Erreur réseau.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    setMsg(""); setError("");
    try {
      const res = await api.delete(`${API_URL}${id}/`);
      if (res.ok) { setMsg("Utilisateur supprimé."); await fetchUsers(); }
      else setError("Erreur suppression.");
    } catch (err) { setError("Erreur réseau"); }
  };

  const toggleAdmin = async (id, currentVal) => {
    setMsg(""); setError("");
    try {
      const res = await api.patch(`${API_URL}${id}/`, { is_staff: !currentVal });
      if (res.ok) { setMsg("Rôle mis à jour."); await fetchUsers(); }
      else setError("Erreur mise à jour rôle.");
    } catch (err) { setError("Erreur réseau"); }
  };

  const exportCSV = () => {
    if (!users.length) return;
    const headers = ["Email", "Username", "Rôle"];
    const rows = users.map(u => [
      u.email,
      u.username,
      u.is_superuser ? "SuperAdmin" : u.is_staff ? "Admin" : "Membre"
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "utilisateurs.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      <p style={{ color: "#eb6424", fontSize: "1.2rem" }}>Chargement...</p>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        minHeight: "100vh",
        background: "#f5f0eb",
        fontFamily: "'Inter', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "center", padding: "28px 0 10px" }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <img src={logo} alt="Logo Polyglow" style={{ width: "163px" }} />
        </Link>
      </div>

      {/* Main content */}
      <div style={{ maxWidth: 900, margin: "0 auto", width: "100%", padding: "20px 24px 60px" }}>

        <h2 style={{ fontSize: "1.6rem", fontWeight: 700, color: "#111", marginBottom: 20 }}>
          Gestion des Utilisateurs
        </h2>

        {/* Messages */}
        {error && (
          <div className="user-message-error">{error}</div>
        )}
        {msg && (
          <div className="user-message-success">{msg}</div>
        )}

        {/* Formulaire ajout */}
        <div style={{
          background: "#fff",
          borderRadius: 16,
          padding: "24px 28px",
          marginBottom: 28,
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ fontSize: "1.15rem", fontWeight: 700, color: "#111", margin: 0 }}>
              Ajouter un utilisateur
            </h3>
            <button
              onClick={exportCSV}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "white", border: "1.5px solid #ddd",
                borderRadius: 10, padding: "8px 14px",
                fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
                color: "#333", transition: "0.2s",
              }}
            >
              ↑ Exporter la liste (CSV)
            </button>
          </div>

          <form onSubmit={handleCreate} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: 6, color: "#222" }}>Email</label>
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #ddd", fontSize: "0.9rem", outline: "none" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: 6, color: "#222" }}>Username</label>
              <input
                type="text" required value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #ddd", fontSize: "0.9rem", outline: "none" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: 6, color: "#222" }}>Mot de passe</label>
              <input
                type="password" required autoComplete="new-password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #ddd", fontSize: "0.9rem", outline: "none" }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 2 }}>
              <input
                type="checkbox" id="is_staff" checked={form.is_staff}
                onChange={(e) => setForm({ ...form, is_staff: e.target.checked })}
                style={{ width: 16, height: 16, accentColor: "#eb6424", cursor: "pointer" }}
              />
              <label htmlFor="is_staff" style={{ fontSize: "0.9rem", fontWeight: 600, cursor: "pointer" }}>Staff?</label>
            </div>
            <button
              type="submit"
              style={{
                padding: "10px 22px", background: "#eb6424", color: "white",
                border: "none", borderRadius: 10, fontWeight: 700,
                fontSize: "0.95rem", cursor: "pointer",
              }}
            >
              Créer
            </button>
          </form>
        </div>

        {/* Tableau utilisateurs */}
        <div style={{
          background: "#fff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#eb6424", color: "white" }}>
                <th style={{ padding: "14px 18px", textAlign: "left", fontWeight: 700, fontSize: "0.95rem" }}>Email</th>
                <th style={{ padding: "14px 18px", textAlign: "left", fontWeight: 700, fontSize: "0.95rem" }}>Username</th>
                <th style={{ padding: "14px 18px", textAlign: "left", fontWeight: 700, fontSize: "0.95rem" }}>Rôle</th>
                <th style={{ padding: "14px 18px", textAlign: "left", fontWeight: 700, fontSize: "0.95rem" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ padding: 20, textAlign: "center", color: "#888" }}>
                    Aucun utilisateur trouvé.
                  </td>
                </tr>
              ) : (
                users.map((u, i) => (
                  <tr key={u.id} style={{
                    borderBottom: "1px solid #f0f0f0",
                    background: i % 2 === 0 ? "#fff" : "#fafafa",
                    transition: "background 0.15s",
                  }}>
                    <td style={{ padding: "14px 18px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: "50%",
                          background: "#f0ece8", display: "flex",
                          alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: "0.9rem" }}>{u.email}</span>
                      </div>
                    </td>
                    <td style={{ padding: "14px 18px", fontSize: "0.9rem" }}>{u.username}</td>
                    <td style={{ padding: "14px 18px" }}>
                      {u.is_superuser ? (
                        <span style={{ color: "#eb6424", fontWeight: 800, fontSize: "0.95rem" }}>SuperAdmin</span>
                      ) : (
                        <button
                          onClick={() => toggleAdmin(u.id, u.is_staff)}
                          style={{
                            background: u.is_staff ? "#f59e0b" : "#12b3c7",
                            color: "white", border: "none",
                            padding: "7px 16px", borderRadius: 999,
                            fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                            minWidth: 140,
                          }}
                        >
                          {u.is_staff ? "Admin (Retirer)" : "Membre (Promouvoir)"}
                        </button>
                      )}
                    </td>
                    <td style={{ padding: "14px 18px" }}>
                      {u.is_superuser ? (
                        <span style={{ color: "#aaa", fontSize: "0.85rem" }}>N/A</span>
                      ) : (
                        <button
                          onClick={() => handleDelete(u.id)}
                          style={{
                            background: "none", border: "none",
                            cursor: "pointer", display: "flex",
                            alignItems: "center", gap: 6,
                            color: "#e53935", fontWeight: 700, fontSize: "0.9rem",
                            padding: "6px 10px", borderRadius: 8,
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "#fdecea"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e53935" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                          Supprimer
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "16px 0", color: "#aaa", fontSize: "0.85rem", marginTop: "auto" }}>
        © 2024 polyglow
      </div>
    </motion.div>
  );
}